import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BemClass from '@upsales/components/Utils/bemClass';
import './MentionsInput.scss';
import { useSelector } from 'react-redux';
import { RootState } from 'Store/index';
import { EditorState, ContentState, convertToRaw, RawDraftContentState, convertFromRaw } from 'draft-js';
import PluginEditor from '@draft-js-plugins/editor';
import createMentionPlugin, { defaultSuggestionsFilter, MentionData } from '@draft-js-plugins/mention';
import { type BasicUserWithPermissions } from 'App/resources/Model/User';

interface Props {
	onChange: (val: string) => void;
	value: string;
	editorRef?: (r: PluginEditor | null) => void;
	editorProps?: Partial<React.ComponentProps<typeof PluginEditor>>;
	className?: string;
	onEnter?: Function;
}

const convertToSaveFormat = (editorState: EditorState) => {
	const raw = convertToRaw(editorState?.getCurrentContent());

	let result = '';

	for (let i = 0; i < raw.blocks.length; i++) {
		const block = raw.blocks[i];
		let blockText = block.text;

		if (block.entityRanges && block.entityRanges.length > 0) {
			const sortedRanges = [...block.entityRanges].sort((a, b) => a.offset - b.offset);

			let processedText = '';
			let lastIndex = 0;

			sortedRanges.forEach(range => {
				const entity = raw.entityMap[range.key];

				if (entity && entity.type === 'mention') {
					processedText += blockText.slice(lastIndex, range.offset);

					const mentionData = entity.data;
					if (mentionData.mention?.tag) {
						processedText += mentionData.mention.tag;
					}

					lastIndex = range.offset + range.length;
				}
			});

			processedText += blockText.slice(lastIndex);
			blockText = processedText;
		}

		result += blockText;

		if (i < raw.blocks.length - 1) {
			const nextBlock = raw.blocks[i + 1];
			const nextBlockText = nextBlock.text;

			if (blockText.endsWith('\n') && nextBlockText === '') {
				result += '\n';
			}

			result += '\n';
		}
	}

	return result;
};

const formatToUpsalesMention = (entity: MentionData) => {
	return { ...entity, tag: `@__user$${entity.id}__` };
};

const decodeHtmlEntities = (text: string): string => {
	const textarea = document.createElement('textarea');
	textarea.innerHTML = text;
	return textarea.value;
};

type SuggestionState = { user: MentionData[] };
export const getEditorStateFromValue = (value: string, users: BasicUserWithPermissions[]) => {
	if (!value) {
		return EditorState.createEmpty();
	}

	// This handles cases where descriptions are saved with HTML markup (e.g., from ticket creation)
	let normalizedValue = value
		.replace(/<br\s*\/?>/gi, '\n') // Convert <br> to newlines
		.replace(/<\/?(p|div|li|ul|ol|h[1-6]|blockquote)[^>]*>/gi, '\n') // Convert block elements to newlines
		.replace(/<[^>]+>/g, ''); // Strip all remaining HTML tags

	normalizedValue = decodeHtmlEntities(normalizedValue);

	const tagRegex = /@__(?<entity>user)\$(?<id>\d+)__/g;
	// get mentions from initial value
	//TODO fetch entities from found mentions. First iteration just users so no need to fetch
	const foundMentions = [...Array.from(normalizedValue.matchAll(tagRegex))];

	// add user data to array. TODO should support other entities
	const entityMap = foundMentions.reduce((res: RawDraftContentState['entityMap'], m, index) => {
		if (m?.groups?.id) {
			// only users for now, entitytype exist on m.groups.entity
			const entityData = users.find(usr => usr.id === parseInt(m.groups?.id as string));
			if (entityData) {
				res[index] = {
					type: 'mention',
					mutability: 'IMMUTABLE',
					data: { mention: formatToUpsalesMention(entityData) }
				};
			}
		}

		return res;
	}, {});

	const rawContent = convertToRaw(ContentState.createFromText(normalizedValue));
	rawContent.entityMap = entityMap;

	rawContent.blocks = rawContent.blocks.map(block => {
		let text = block.text;
		//
		//replace tag values with entity text
		const ranges = [];
		for (let index = 0; index < foundMentions.length; index++) {
			const mention = foundMentions[index];
			const tagIdx = text.indexOf(mention[0]);
			if (tagIdx !== -1) {
				const user = users.find(u => u.id === parseInt(mention?.groups?.id as string));

				text = text.replace(mention[0], user?.name ?? '');
				ranges.push({ offset: tagIdx, key: index, length: user?.name.length ?? 0 });
			}
		}

		return { ...block, text, entityRanges: ranges };
	});

	return EditorState.createWithContent(convertFromRaw(rawContent));
};

// only supports users for now
const MentionsInput = ({ value, onChange, editorRef, editorProps, className = '', onEnter }: Props) => {
	const classes = new BemClass('MentionsInput', className);
	const users = useSelector((state: RootState) => state.App.userMap.active);
	const activeUsers = useMemo(() => users.filter(user => user.active && !user.ghost), [users]);
	const [open, setOpen] = useState(false);
	const [editorState, setEditorState] = useState(() => getEditorStateFromValue(value, users));
	const [suggestions, setSuggestions] = useState<SuggestionState>({
		user: activeUsers.map(formatToUpsalesMention)
	});
	const ref = React.useRef<PluginEditor | null>(null);

	// if parent sets comment to '' it should empty the editor state as well. Need to come up with some solution to be able to set to whatever value you like from parent
	useEffect(() => {
		if (!value && editorState.getCurrentContent().getPlainText()) {
			setEditorState(EditorState.push(editorState, ContentState.createFromText(''), 'remove-range'));
		}
	}, [value]);
	const { MentionSuggestions, mentionPlugin } = useMemo(() => {
		const mentionPlugin = createMentionPlugin({
			entityMutability: 'IMMUTABLE',
			supportWhitespace: true,
			mentionTrigger: '@',
			theme: {
				mention: classes.elem('mention').b(),
				mentionSuggestions: classes.elem('mention-suggestions').b(),
				mentionSuggestionsEntry: classes.elem('mention-suggestions-entry').b(),
				mentionSuggestionsEntryAvatar: classes.elem('mention-suggestions-entry-avatar').b(),
				mentionSuggestionsEntryFocused: classes.elem('mention-suggestions-entry-focused').b(),
				mentionSuggestionsEntryText: classes.elem('mention-suggestions-entry-text').b()
			}
		});
		const { MentionSuggestions } = mentionPlugin;
		return { MentionSuggestions, mentionPlugin };
	}, []);
	const onOpenChange = useCallback((_open: boolean) => {
		setOpen(_open);
	}, []);
	const onSearchChange = useCallback(({ value }: { value: string }) => {
		setSuggestions({ user: defaultSuggestionsFilter(value, suggestions.user) });
	}, []);
	return (
		<div className={classes.b()}>
			<PluginEditor
				editorState={editorState}
				onChange={state => {
					onChange(convertToSaveFormat(state));
					setEditorState(state);
				}}
				plugins={[mentionPlugin]}
				ref={r => {
					editorRef?.(r);
					ref.current = r;
				}}
				stripPastedStyles
				keyBindingFn={e => {
					// Disable starting with a new line because it makes everything awkward
					if (e.key === 'Enter' && e.shiftKey && value === '') {
						return '';
					}

					if (e.key === 'Enter' && !e.shiftKey) {
						onEnter?.();
						// need to return some string or it won't override default behaviour
						return '';
					}
				}}
				{...editorProps}
			/>
			<MentionSuggestions
				open={open}
				onOpenChange={onOpenChange}
				suggestions={suggestions.user}
				onSearchChange={onSearchChange}
			/>
		</div>
	);
};

export default MentionsInput;
